import { Instagram, Facebook, Twitter, ArrowUp } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { CONTACT_EMAIL, CONTACT_PHONE, CONTACT_PHONE_HREF } from "@/data";
import { goToSection } from "@/lib/scroll";

const EXPLORE = [
  { label: "Manifesto", href: "#manifesto" },
  { label: "Menu", href: "#menu" },
  { label: "Outlets", href: "#outlets" },
  { label: "Reviews", href: "#reviews" },
  { label: "Franchise Enquiry", href: "#contact" },
];

const LEGAL = [
  { label: "About Us", to: "/about-us" },
  { label: "Contact Us", to: "/contact-us" },
  { label: "Privacy Policy", to: "/privacy-policy" },
  { label: "Terms & Conditions", to: "/terms-conditions" },
  { label: "Refund & Cancellation Policy", to: "/refund-cancellation-policy" },
  { label: "Track Your Order", to: "/track" },
];

export const SiteFooter = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const go = (href) => (e) => {
    e.preventDefault();
    goToSection(navigate, pathname, href);
  };

  return (
  <footer data-testid="site-footer" className="bg-[#1C130E] text-[#FDFBF7] pt-20 lg:pt-28 pb-10 overflow-hidden">
    <div className="max-w-7xl mx-auto px-6 lg:px-12">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-12 pb-16 border-b border-[#FDFBF7]/10">
        <div className="md:col-span-6">
          <p className="font-mono-accent text-[10px] tracking-[0.25em] uppercase text-[#D4A359] mb-4">
            Boulangerie · Pâtisserie
          </p>
          <p className="font-display italic text-2xl sm:text-3xl leading-snug max-w-md text-[#FDFBF7]/85">
            "Butter, flour and patience — baked into every morning."
          </p>
          <div className="mt-8 flex items-center gap-3">
            {[Instagram, Facebook, Twitter].map((Icon, i) => (
              <a
                key={i}
                href="#top"
                data-testid={`footer-social-${i}`}
                onClick={(e) => e.preventDefault()}
                aria-label="Social link"
                className="grid place-items-center w-10 h-10 rounded-full border border-[#FDFBF7]/20 hover:bg-[#C85A32] hover:border-[#C85A32] transition-colors"
              >
                <Icon size={16} />
              </a>
            ))}
          </div>
        </div>
        <div className="md:col-span-3">
          <p className="font-mono-accent text-[10px] tracking-[0.25em] uppercase text-[#FDFBF7]/45 mb-5">Explore</p>
          <ul className="space-y-3">
            {EXPLORE.map((l) => (
              <li key={l.href}>
                <a
                  href={l.href}
                  data-testid={`footer-${l.label.toLowerCase().replace(/\s+/g, "-")}-link`}
                  onClick={go(l.href)}
                  className="text-sm text-[#FDFBF7]/70 hover:text-[#C85A32] transition-colors"
                >
                  {l.label}
                </a>
              </li>
            ))}
          </ul>
        </div>
        <div className="md:col-span-3">
          <p className="font-mono-accent text-[10px] tracking-[0.25em] uppercase text-[#FDFBF7]/45 mb-5">
            Say Bonjour
          </p>
          <a
            href={`mailto:${CONTACT_EMAIL}`}
            data-testid="footer-email-link"
            className="block w-fit text-sm text-[#FDFBF7]/70 hover:text-[#C85A32] transition-colors"
          >
            {CONTACT_EMAIL}
          </a>
          <a
            href={CONTACT_PHONE_HREF}
            data-testid="footer-phone-link"
            className="block w-fit text-sm text-[#FDFBF7]/70 hover:text-[#C85A32] transition-colors mt-2"
          >
            {CONTACT_PHONE}
          </a>
          <button
            data-testid="footer-back-to-top-btn"
            onClick={go("#top")}
            className="group mt-8 inline-flex items-center gap-2 text-sm font-semibold text-[#FDFBF7]/80 hover:text-[#C85A32] transition-colors"
          >
            Back to top
            <span className="grid place-items-center w-9 h-9 rounded-full border border-[#FDFBF7]/20 group-hover:border-[#C85A32] transition-colors">
              <ArrowUp size={14} className="transition-transform duration-300 group-hover:-translate-y-0.5" />
            </span>
          </button>
        </div>
      </div>
      <div className="flex flex-wrap justify-center gap-x-7 gap-y-2 mt-12" data-testid="footer-legal-links">
        {LEGAL.map((l) => (
          <button
            key={l.to}
            type="button"
            data-testid={`footer-legal-${l.to.split("/")[1]}-link`}
            onClick={() => {
              navigate(l.to);
              window.scrollTo(0, 0);
              if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
            }}
            className="font-mono-accent text-[10px] tracking-[0.2em] uppercase text-[#FDFBF7]/50 hover:text-[#C85A32] transition-colors"
          >
            {l.label}
          </button>
        ))}
      </div>
      <p className="font-display font-medium tracking-tight text-[19vw] leading-[0.85] text-center text-[#FDFBF7]/[0.07] select-none pointer-events-none mt-10" aria-hidden="true">
        French Pie
      </p>
      <p className="text-center font-mono-accent text-[10px] tracking-[0.2em] uppercase text-[#FDFBF7]/40 mt-6">
        © {new Date().getFullYear()} French Pie · frenchpie.co.in — Crafted with butter
      </p>
      <p className="text-center font-mono-accent text-[10px] tracking-[0.15em] uppercase text-[#FDFBF7]/35 mt-2" data-testid="footer-compliance">
        GSTIN 01HBLPD9801F1ZX · FSSAI Reg. No. 21023418000068 · Udyam UDYAM-JK-21-0075571
      </p>
      <p className="text-center text-xs text-[#FDFBF7]/50 mt-3" data-testid="footer-credit">
        Designed &amp; Developed by <span className="text-[#D4A359] font-semibold">Talib Shakeel Naqash</span>
      </p>
    </div>
  </footer>
  );
};
